import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  Chip
} from '@mui/material';
import { Icon } from '@iconify/react';

const ReportDownload = ({
  reportType = 'analytics', // 'analytics', 'revenue', 'transactions', 'roaming', 'comprehensive'
  reportTitle = 'Download Report',
  buttonVariant = 'contained',
  buttonSize = 'medium',
  iconOnly = false,
  tooltipTitle,
  buttonSx = {},
  onDownload,
  disabled = false,
  loading = false,
  filters = {},
  showFormatOptions = true,
  customFormats = [],
  sx = {},
  // New props for useDownload hook integration
  downloadFunction,
  isDownloading = false,
  downloadError = null
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [downloadingFormat, setDownloadingFormat] = useState(null);
  const open = Boolean(anchorEl);

  // Default report formats
  const defaultFormats = [
    {
      type: 'pdf',
      label: 'PDF Report',
      icon: 'eva:file-text-outline',
      description: 'Formatted report for presentations',
      color: 'error'
    },
    {
      type: 'excel',
      label: 'Excel Spreadsheet',
      icon: 'eva:file-outline',
      description: 'Data for analysis and calculations',
      color: 'primary'
    },
    {
      type: 'csv',
      label: 'CSV Data',
      icon: 'eva:download-fill',
      description: 'Raw data for import/export',
      color: 'info'
    }
  ];

  // Merge custom formats with default formats
  const availableFormats = customFormats.length > 0 ? customFormats : defaultFormats;

  const handleClick = (event) => {
    if (showFormatOptions && availableFormats.length > 1) {
      setAnchorEl(event.currentTarget);
    } else {
      // If only one format or no format options, download directly
      const format = availableFormats[0] || defaultFormats[0];
      handleDownload(format.type);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = async (format) => {
    setDownloadingFormat(format);
    handleClose();

    try {
      const downloadParams = {
        reportType,
        format,
        filters,
        timestamp: new Date().toISOString()
      };

      if (downloadFunction) {
        // Use the provided download function (from useDownload hook)
        await downloadFunction(downloadParams);
      } else if (onDownload) {
        // Use the legacy onDownload callback
        await onDownload(downloadParams);
      } else {
        // Default download behavior
        await defaultDownloadBehavior(downloadParams);
      }
    } catch (error) {
      console.error('Download failed:', error);
      // You could add a toast notification here
    } finally {
      setDownloadingFormat(null);
    }
  };

  const defaultDownloadBehavior = async (params) => {
    // This would be implemented based on your API structure
    console.log('Downloading report with params:', params);
    
    // Example API call structure:
    // const response = await fetch('/api/reports/download', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(params)
    // });
    
    // const blob = await response.blob();
    // const url = window.URL.createObjectURL(blob);
    // const a = document.createElement('a');
    // a.href = url;
    // a.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.${params.format}`;
    // document.body.appendChild(a);
    // a.click();
    // window.URL.revokeObjectURL(url);
    // document.body.removeChild(a);
  };

  const getReportIcon = () => {
    const iconMap = {
      analytics: 'eva:bar-chart-fill',
      revenue: 'eva:credit-card-fill',
      transactions: 'eva:activity-fill',
      roaming: 'eva:globe-fill',
      comprehensive: 'eva:pie-chart-fill'
    };
    return iconMap[reportType] || 'eva:download-fill';
  };

  const getReportLabel = () => {
    if (iconOnly) return null;
    
    const labelMap = {
      analytics: 'Download Analytics Report',
      revenue: 'Download Revenue Report',
      transactions: 'Download Transaction Report',
      roaming: 'Download Roaming Report',
      comprehensive: 'Download Comprehensive Report'
    };
    return labelMap[reportType] || reportTitle;
  };

  const isDownloadingState = loading || isDownloading || downloadingFormat !== null;

  const buttonContent = iconOnly ? (
    <Icon icon={getReportIcon()} width={22} height={22} />
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isDownloadingState ? (
        <CircularProgress size={16} color="inherit" />
      ) : (
        <Icon icon={getReportIcon()} width={20} height={20} />
      )}
      <Box
        component="span"
        sx={{
          display: { xs: "none", sm: "inline" },
          textAlign: 'center',
        }}
      >
        {isDownloadingState ? 'Downloading...' : getReportLabel()}
      </Box>
    </Box>
  );

  return (
    <Box sx={sx}>
      <Tooltip title={tooltipTitle || getReportLabel()} arrow placement="top">
        <Button
          variant={buttonVariant}
          size={buttonSize}
          onClick={handleClick}
          disabled={disabled || isDownloadingState}
          sx={{
            minWidth: iconOnly ? 'auto' : 'fit-content',
            ...buttonSx
          }}
        >
          {buttonContent}
        </Button>
      </Tooltip>

      {showFormatOptions && availableFormats.length > 1 && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              minWidth: 200,
              mt: 1
            }
          }}
        >
          {availableFormats.map((format) => (
            <MenuItem
              key={format.type}
              onClick={() => handleDownload(format.type)}
              disabled={isDownloadingState}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1.5
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {downloadingFormat === format.type ? (
                  <CircularProgress size={20} />
                ) : (
                  <Icon icon={format.icon} width={20} height={20} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={format.label}
                secondary={format.description}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
                secondaryTypographyProps={{
                  fontSize: '0.75rem',
                  color: 'text.secondary'
                }}
              />
              <Chip
                label={format.type.toUpperCase()}
                size="small"
                color={format.color}
                sx={{ ml: 1, fontSize: '0.65rem' }}
              />
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
};

export default ReportDownload;
